import { useInitializePractice } from '@/hooks/useInitializePractice';
import { useSoulMapLearningPath } from '@/hooks/queries/learning-paths';
import { Icon } from '@iconify/react';
import { Button, Spinner } from '@nextui-org/react';

const soulMapHighlights = [
  'A calm space to reflect on what matters to you',
  'Guided conversation to explore challenges and next steps',
  'Pick up past chats anytime from Recent Chats',
];

const SoulMapHomePage = () => {
  const { prompt: soulMapPrompt, isLoading: isLoadingSoulMapPath } =
    useSoulMapLearningPath({ stepIndex: 0 });
  const { createPracticeAndNavigate } = useInitializePractice(undefined, {
    promptsEnabled: false,
  });

  const agentExists = Boolean(soulMapPrompt?.name);

  const handleStartChat = () => {
    if (!soulMapPrompt?.name) return;
    void createPracticeAndNavigate({ name: soulMapPrompt.name });
  };

  if (isLoadingSoulMapPath) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-5xl">
        <div className="text-center md:text-left">
          <h1 className="text-3xl font-semibold text-foreground">
            Chat with Soul Map
          </h1>
          <p className="mt-2 text-sm text-default-400">
            Your personal space for reflection and guided conversation
          </p>
        </div>

        <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-stretch md:gap-10">
          <div className="flex h-full w-full flex-col justify-center text-center md:w-1/2 md:text-left">
            <div className="space-y-4">
              <p className="text-base text-default-500 leading-relaxed">
                Start a new conversation whenever you are ready.
              </p>
              <p className="text-sm text-default-400 leading-relaxed">
                There is no right way to begin — share what is on your mind and
                Soul Map will meet you where you are.
              </p>
              <p className="text-sm text-default-400 leading-relaxed">
                Whether you are reflecting on a challenging moment, exploring
                what has shaped you, or simply checking in with yourself, Soul
                Map offers a thoughtful, supportive space to explore what
                matters.
              </p>
              <p className="text-sm text-default-400 leading-relaxed">
                Take your time. You can pause at any point and return later —
                your conversations stay with you in Recent Chats, ready whenever
                you want to continue.
              </p>
            </div>
          </div>

          <div className="flex h-full w-full flex-col rounded-2xl border border-default-200 bg-default-50 px-5 py-5 md:w-1/2 md:px-6">
            <p className="text-sm font-medium text-foreground">
              What you can do here
            </p>
            <ul className="mt-4 space-y-3">
              {soulMapHighlights.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-sm text-default-600"
                >
                  <Icon
                    icon="solar:check-circle-bold"
                    className="mt-0.5 shrink-0 text-green-500"
                    width={17}
                    height={17}
                  />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 border-t border-default-200 pt-4 text-xs leading-relaxed text-default-400">
              Conversations are private to your account. You can return anytime
              to continue where you left off.
            </p>
          </div>
        </div>

        <div className="mt-10 flex justify-center md:justify-start">
          {!agentExists ? (
            <p className="text-sm text-danger">
              Soul Map isn’t available just yet. Please check back soon.
            </p>
          ) : (
            <Button
              color="primary"
              radius="full"
              size="lg"
              className="min-w-[220px]"
              onPress={handleStartChat}
            >
              Start a conversation
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SoulMapHomePage;
